import { isTerminalOrderStatus } from "@loot-os/fulfillment-core/order/state-machine";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import { Skeleton } from "@loot-os/ui/components/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@loot-os/ui/components/table";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";

import { ErrorAlert } from "../../components/ErrorAlert";
import { OrderCancelDialog } from "../../components/OrderCancelDialog";
import { OrderTransitionActions } from "../../components/OrderTransitionActions";
import { centsToDollars, dashIfEmpty, formatDate } from "../../lib/format";
import { readMarketplaceSnapshot } from "../../lib/marketplaceLine";
import { useTRPC } from "../../lib/trpc";

const LINE_COLUMNS = [
  "Item",
  "Source",
  "Size",
  "Price basis",
  "Unit value",
  "Product",
  "Status",
  "",
] as const;

/**
 * One fulfillment order: what a fulfiller reads before placing the buy. The router
 * reuses this page when only `$orderId` changes, so the two children that hold form
 * state are keyed by the order id and a move to another order clears them.
 */
export function OrderPage() {
  const trpc = useTRPC();
  const { orderId } = useParams({ from: "/app/fulfillment/orders/$orderId" });
  const detail = useQuery(trpc.platform.fulfillment.orders.get.queryOptions({ id: orderId }));

  if (detail.isLoading) return <Skeleton className="h-96 w-full" />;
  if (detail.error) return <ErrorAlert message={detail.error.message} />;
  if (!detail.data) return null;

  const { order, lines } = detail.data;
  const isOpen = !isTerminalOrderStatus(order.status);
  const activeLines = lines.filter((line) => line.status !== "CANCELLED");
  const facts: [string, string][] = [
    ["Tenant", order.tenantId],
    ["Shopper", order.endUserId],
    ["Type", order.type],
    ["Status", order.status],
    ["Method", dashIfEmpty(order.method)],
    ["Vendor", dashIfEmpty(order.vendorId)],
    ["Total", order.totalCost === null ? "—" : centsToDollars(order.totalCost)],
    ["Shipping fee", centsToDollars(order.shippingFee)],
    ["Tracking", dashIfEmpty(order.trackingNumber)],
    ["Tenant reference", dashIfEmpty(order.tenantReferenceId)],
    ["Created", formatDate(order.createdAt)],
    ["Shipped", formatDate(order.shippedAt)],
    ["Cancelled", formatDate(order.cancelledAt)],
    ["Cancellation code", dashIfEmpty(order.cancellationCode)],
    ["Cancellation reason", dashIfEmpty(order.cancellationReason)],
  ];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Order</h1>
          <p className="font-mono text-xs text-muted-foreground">{order.id}</p>
        </div>
        {isOpen ? <OrderCancelDialog key={order.id} orderId={order.id} /> : null}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Order</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
          {facts.map(([label, value]) => (
            <div key={label} className="flex justify-between gap-4 text-sm">
              <span className="text-muted-foreground">{label}</span>
              <span className="text-right break-all">{value}</span>
            </div>
          ))}
        </CardContent>
      </Card>

      <OrderTransitionActions key={order.id} order={order} />

      <Card>
        <CardHeader>
          <CardTitle>Lines</CardTitle>
          <CardDescription>
            Price basis is what the tenant quoted its shopper; a live price above it is refused.
          </CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {LINE_COLUMNS.map((column) => (
                  <TableHead key={column}>{column}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {lines.map((line) => {
                const snapshot = readMarketplaceSnapshot(line.snapshot);
                return (
                  <TableRow key={line.id}>
                    <TableCell className="font-mono text-xs">{line.itemId}</TableCell>
                    <TableCell>{line.itemSource}</TableCell>
                    <TableCell>
                      {dashIfEmpty(snapshot.variantValue ?? snapshot.variantId)}
                    </TableCell>
                    <TableCell>
                      {snapshot.listingPrice === undefined
                        ? "—"
                        : centsToDollars(snapshot.listingPrice)}
                    </TableCell>
                    <TableCell>{centsToDollars(line.unitValue)}</TableCell>
                    <TableCell>
                      {snapshot.productUrl ? (
                        <a
                          className="underline underline-offset-4"
                          href={snapshot.productUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Open
                        </a>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>{line.status}</TableCell>
                    <TableCell className="text-right">
                      {isOpen && line.status !== "CANCELLED" ? (
                        <OrderCancelDialog
                          orderId={order.id}
                          line={{ id: line.id, isLastActive: activeLines.length === 1 }}
                        />
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Destination</CardTitle>
        </CardHeader>
        <CardContent>
          {order.shippingAddress ? (
            <pre className="font-mono text-xs break-words whitespace-pre-wrap">
              {JSON.stringify(order.shippingAddress, null, 2)}
            </pre>
          ) : (
            <p className="text-sm text-muted-foreground">No address on this order.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
